import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorNotice, PageHeading, Panel, SectionHeader, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  DISQUALIFY_LABEL,
  LEAD_PANEL_QUERY_KEY,
  LEAD_PANEL_REFRESH_MS,
  assignLead,
  disqualifyLead,
  readLeadPanel,
  type DisqualifyReason,
  type PanelLead,
} from '@/lib/lead-panel';
import { ConvertLeadDialog } from '@/components/lead-panel/ConvertLeadDialog';
import { QueueTable } from '@/components/lead-panel/QueueTable';
import { RecentLeadsTable } from '@/components/lead-panel/RecentLeadsTable';
import { RepLoadTable } from '@/components/lead-panel/RepLoadTable';

/** The route, named once (App.tsx and the nav use it). */
export const LEAD_PANEL_PATH = '/lead-panel';

type PanelAction =
  | { readonly kind: 'assign'; readonly lead: PanelLead; readonly ownerId: string }
  | { readonly kind: 'disqualify'; readonly lead: PanelLead; readonly reason: DisqualifyReason };

function leadName(lead: PanelLead): string {
  return lead.company || lead.contactName || 'The lead';
}

/**
 * Sales → Lead Control Panel (replaces the Salesforce Visualforce tab). The
 * leads waiting for a rep, each rep's load, and today's and yesterday's
 * leads. With the manage_lead_assignment grant (Jessica) a waiting lead is
 * given to a rep or marked spam / not a fit in one click; anyone converts
 * their own open lead with the same preview BEStie gives. Refreshes every
 * minute.
 *
 * @returns The panel page.
 */
export function LeadPanelPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [converting, setConverting] = useState<PanelLead | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const panel = useQuery({
    queryKey: LEAD_PANEL_QUERY_KEY.root,
    queryFn: ({ signal }) => readLeadPanel(signal),
    refetchInterval: LEAD_PANEL_REFRESH_MS,
  });

  const action = useMutation({
    mutationFn: (input: PanelAction) =>
      input.kind === 'assign' ? assignLead(input.lead.id, input.ownerId) : disqualifyLead(input.lead.id, input.reason),
    onSuccess: async (lead, input) => {
      setResult(
        input.kind === 'assign'
          ? `${leadName(input.lead)} went to ${lead.ownerName ?? 'the rep'}.`
          : `${leadName(input.lead)} is marked ${DISQUALIFY_LABEL[input.reason].status} and left the queue.`,
      );
      await queryClient.invalidateQueries({ queryKey: LEAD_PANEL_QUERY_KEY.root });
    },
  });

  const data = panel.data;
  // Everyone the org lists in its Sales group can receive a lead; only reps
  // with open work get a row (the group also holds people who sell rarely).
  const repsWithWork = (data?.reps ?? []).filter(
    (rep) => rep.openOpportunities + rep.openLeads + rep.openTasks + rep.receivedToday + rep.receivedYesterday > 0,
  );

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Lead Control Panel"
        subtitle="New leads waiting for a rep, what each rep is carrying, and today's and yesterday's leads. Refreshes every minute."
      />

      {result ? (
        <Panel className="mb-4 flex items-center justify-between gap-3 p-4 text-portal-small text-portal-ink">
          <span>{result}</span>
          <Button variant="ghost" size="compact" onClick={() => setResult(null)}>
            Dismiss
          </Button>
        </Panel>
      ) : null}
      {action.isError ? (
        <div className="mb-4">
          <ErrorNotice inline message={errorMessage(action.error)} />
        </div>
      ) : null}

      {panel.isError ? (
        <ErrorNotice title="Lead Control Panel unavailable" message={errorMessage(panel.error)} />
      ) : !data ? (
        <SkeletonTable rows={8} />
      ) : (
        <div className="space-y-8">
          <div>
            <QueueTable
              title={`Waiting for a rep (${data.queue.length.toLocaleString('en-US')})`}
              description={
                data.canManage
                  ? 'Give each lead to a rep, or mark spam and leads that are not a fit. Flagged leads match the patterns of past spam.'
                  : 'Jessica gives these to reps. Flagged leads match the patterns of past spam.'
              }
              panel={data}
              reps={data.reps}
              pending={action.isPending}
              onAssign={(lead, ownerId) => action.mutate({ kind: 'assign', lead, ownerId })}
              onDisqualify={(lead, reason) => action.mutate({ kind: 'disqualify', lead, reason })}
            />
          </div>

          {repsWithWork.length > 0 ? (
            <div>
              <SectionHeader
                title="Reps"
                description="Leads received today and yesterday, open leads, open opportunities by stage and overdue tasks, to pick who gets the next lead."
              />
              <RepLoadTable reps={repsWithWork} stages={data.stages} />
            </div>
          ) : null}

          <div>
            <RecentLeadsTable
              title="Today and yesterday"
              description="Every lead the reps received, converted or not. Convert the open ones here."
              panel={data}
              onConvert={setConverting}
            />
          </div>
        </div>
      )}

      <ConvertLeadDialog
        lead={converting}
        onClose={() => setConverting(null)}
        onConverted={() => void queryClient.invalidateQueries({ queryKey: LEAD_PANEL_QUERY_KEY.root })}
      />
    </section>
  );
}
